import React, { useState, useEffect } from 'react';
import {UserX, Users, Loader2, ArrowLeft, ArrowRight, Filter} from 'lucide-react';import {Button} from '@/components/ui/button';import {useAdminTheme} from '@/contexts/AdminThemeContext';import AdminLayout from './AdminLayout';
import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';

const REASON_LABELS = {
  too_expensive: 'Too expensive',
  not_useful: 'Not useful',
  not_accurate_enough: 'Not accurate enough',
  found_alternative: 'Found alternative',
  privacy_concerns: 'Privacy concerns',
  technical_issues: 'Technical issues',
  taking_a_break: 'Taking a break',
  other: 'Other',
};

export default function AdminChurnedUsersPage() {
  const { isDark, colors } = useAdminTheme();
  const [filter, setFilter] = useState('all');
  const [data, setData] = useState({ churned_users: [], total: 0, page: 1, pages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    axios.get(`${API}/admin/churned-users/stats`, { withCredentials: true })
      .then(res => setStats(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/churned-users`, {
      params: { type: filter, page, limit: 15 },
      withCredentials: true,
    })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filter, page]);

  const cardStyle = { background: colors.card, border: `1px solid ${colors.cardBorder}` };
  const rowStyle = { background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-churned-page">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>Churned Users</h1>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Users who deleted accounts or cancelled subscriptions</p>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="churned-stats">
            <StatCard colors={colors} label="Deleted" value={stats.deleted} color="#ef4444" isDark={isDark} />
            <StatCard colors={colors} label="Unsubscribed" value={stats.unsubscribed} color="#f59e0b" isDark={isDark} />
            <StatCard colors={colors} label="Reactivated" value={stats.reactivated} color="#22c55e" isDark={isDark} />
            <StatCard colors={colors} label="Total Churned" value={stats.deleted + stats.unsubscribed} color="#6366f1" isDark={isDark} />
          </div>
        )}

        {/* Deletion reasons breakdown */}
        {stats?.deletion_reasons && Object.keys(stats.deletion_reasons).length > 0 && (
          <div className="rounded-2xl p-5" style={cardStyle} data-testid="deletion-reasons">
            <h3 className="text-sm font-semibold mb-3" style={{ color: colors.text }}>Deletion Reasons</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.deletion_reasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                <span
                  key={reason}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={rowStyle}
                >
                  <span style={{ color: colors.text }}>{REASON_LABELS[reason] || reason}</span>
                  <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>{count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" style={{ color: colors.textMuted }} />
          {['all', 'deleted', 'unsubscribed'].map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition"
              style={filter === f
                ? { background: 'rgba(99,102,241,0.2)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }
                : { background: colors.input, color: colors.textSecondary, border: `1px solid ${colors.inputBorder}` }
              }
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : f === 'deleted' ? 'Deleted' : 'Unsubscribed'}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="rounded-2xl overflow-hidden" style={cardStyle}>
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: colors.textMuted }} /></div>
          ) : data.churned_users.length === 0 ? (
            <div className="py-12 text-center">
              <UserX className="h-8 w-8 mx-auto mb-2" style={{ color: colors.textMuted }} />
              <p className="text-sm" style={{ color: colors.textMuted }}>No churned users yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="churned-table">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                    <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: colors.textMuted }}>User</th>
                    <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: colors.textMuted }}>Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: colors.textMuted }}>Former Tier</th>
                    <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: colors.textMuted }}>Reason</th>
                    <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: colors.textMuted }}>Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: colors.textMuted }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.churned_users.map((u, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-sm" style={{ color: colors.text }}>{u.name || 'Unknown'}</p>
                        <p className="text-xs" style={{ color: colors.textMuted }}>{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="px-2 py-1 rounded-md text-xs font-medium"
                          style={u.type === 'deleted'
                            ? { background: 'rgba(239,68,68,0.12)', color: '#ef4444' }
                            : { background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }
                          }
                        >
                          {u.type === 'deleted' ? 'Deleted' : 'Unsubscribed'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs capitalize" style={{ color: colors.textSecondary }}>{u.former_tier || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: colors.textSecondary }}>{REASON_LABELS[u.reason] || u.reason || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: colors.textSecondary }}>
                          {u.deleted_at ? new Date(u.deleted_at).toLocaleDateString() : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.reactivated ? (
                          <span className="px-2 py-1 rounded-md text-xs font-medium" style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>Reactivated</span>
                        ) : u.type === 'deleted' ? (
                          <span className="px-2 py-1 rounded-md text-xs font-medium" style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171' }}>
                            Purge {u.purge_scheduled_at ? new Date(u.purge_scheduled_at).toLocaleDateString() : '30d'}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: colors.textMuted }}>
                            {u.access_until ? `Access until ${new Date(u.access_until).toLocaleDateString()}` : 'Cancelled'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {data.pages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-lg gap-1"
              style={{ color: colors.textSecondary, borderColor: colors.inputBorder }}
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <span className="text-xs" style={{ color: colors.textMuted }}>Page {page} of {data.pages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(data.pages, p + 1))}
              disabled={page >= data.pages}
              className="rounded-lg gap-1"
              style={{ color: colors.textSecondary, borderColor: colors.inputBorder }}
            >
              Next <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ colors, label, value, color, isDark }) {
  return (
    <div className="rounded-xl p-4" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{label}</p>
    </div>
  );
}
